import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { gradeTypes } from '$variables/constants';
import Button from '$components/Button';
import Modal from '$components/Modal';

import PercentageGrading from './components/PercentageGrading';
import FreeFormGrading from './components/FreeFormGrading';
import PassFailGrading from './components/PassFailGrading';
import trimString from './components/utils/trimString';

import { ModalContent, ErrorIcon, Wrapper } from './EditGrade.style';

const sanitizeNullGrade = (grade) => (grade === null ? undefined : grade);

const EditGrade = ({
  grade,
  onSave,
  studentId,
  gradeType,
  assignmentId,
  gradesHaveBeenReleased,
  error,
}) => {
  const [gradeValue, setGradeValue] = useState(sanitizeNullGrade(grade));

  useEffect(() => {
    // update when grade is changed from csv upload
    // to remove from input field, we cannot use null
    // we have to use undefined
    setGradeValue(sanitizeNullGrade(grade));
  }, [grade]);

  const [gradeToConfirm, setGradeToConfirm] = useState(null);

  const saveOrConfirm = (args) => {
    if (gradesHaveBeenReleased) {
      setGradeToConfirm(args);
    } else {
      onSave(args);
    }
  };

  const acceptConfirmation = (e) => {
    e.stopPropagation();
    onSave(gradeToConfirm);
    setGradeToConfirm(null);
  };

  const cancelConfirmation = (e) => {
    e.stopPropagation();
    setGradeValue(grade);
    setGradeToConfirm(null);
  };

  const handleClickPassFail = (value) => {
    if (gradeValue === value) {
      setGradeValue();
      saveOrConfirm({ assignmentId, studentId });
      return;
    }

    setGradeValue(value);
    saveOrConfirm({
      grade: value,
      studentId,
      assignmentId,
    });
  };

  const handleSave = () =>
    saveOrConfirm({
      grade: trimString(gradeValue),
      studentId,
      assignmentId,
    });

  return (
    <Wrapper>
      {error && <ErrorIcon />}
      {gradeType === gradeTypes.percentage && (
        <PercentageGrading
          grade={gradeValue}
          onBlur={handleSave}
          onChange={setGradeValue}
          error={error}
        />
      )}
      {gradeType === gradeTypes.freeform && (
        <FreeFormGrading
          grade={gradeValue}
          onBlur={handleSave}
          onChange={setGradeValue}
          error={error}
        />
      )}
      {gradeType === gradeTypes.passFail && (
        <PassFailGrading
          grade={gradeValue}
          onChange={handleClickPassFail}
          error={error}
        />
      )}
      <Modal
        open={!!gradeToConfirm}
        handleClose={cancelConfirmation}
        handleSubmit={acceptConfirmation}
        headerText="Save and release updated grade?"
        submitText="Yes, save and release"
        footerDecoration={
          <Button
            onClick={cancelConfirmation}
            label="No, undo change"
            size="large"
            look="secondary"
          >
            No, undo change
          </Button>
        }
      >
        <ModalContent>
          The grades for this assignment are already released, so every change
          will be immediately visible to students. Do you want to save this
          change?
        </ModalContent>
      </Modal>
    </Wrapper>
  );
};

EditGrade.propTypes = {
  grade: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSave: PropTypes.func.isRequired,
  studentId: PropTypes.string.isRequired,
  assignmentId: PropTypes.string.isRequired,
  gradeType: PropTypes.string.isRequired,
  gradesHaveBeenReleased: PropTypes.bool,
  error: PropTypes.bool,
};

EditGrade.defaultProps = {
  grade: undefined,
  gradesHaveBeenReleased: false,
  error: false,
};

export default EditGrade;
